import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MDSpinner from "react-md-spinner";
import GoogleMapReact from "google-map-react";
import axios from "axios";
import {
  FaCheck,
  FaDrawPolygon,
  FaMapMarkedAlt,
  FaTh,
  FaTimes,
  FaUndo,
  FaBorderStyle,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

import MapViewportToolbar from "../../components/MapViewportToolbar";
import myConfig from "../../configs/config";
import {
  MAP_VIEWPORT_SCOPES,
  applyViewportToGoogleMap,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  resolveMapViewport,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import {
  closePolygonRing,
  computeCleanFenceSplit,
  computePointsAreaHa,
  computePointsPerimeterM,
  getLandPlotMapStyle,
  getPlotMapLabel,
  landPlotsInsideRing,
  parseLandplotRing,
  pathToPolygonWkt,
  summarizeCleanFenceSelection,
  sumLandPlotAreaHa,
} from "../../utils/mapMeasurements";
import {
  createGroupFarm,
  fetchGroupLandPlots,
} from "./serviceOrderLocationApi";
import { getMapOptions } from "./rumoPistaUtils";
import { useResizablePanel } from "./useResizablePanel";
import ServiceOrderMapOverlay from "./ServiceOrderMapOverlay";
import { releaseModalBlockers } from "../../utils/nestedMuiModalProps";
import "./ServiceOrderLocationMapModal.css";
import "./TrackHeadingMapModal.css";

const MODES = {
  SELECT: "select",
  FARM: "farm",
};

const CLEAN_FENCE_NAME = "Cerca limpa";
const CLEAN_FENCE_ACTION_LABEL = "Usar cerca limpa";
const CLEAN_FENCE_WIDTH_OPTIONS = Array.from({ length: 10 }, (_, index) => index + 1);

const CLEAN_FENCE_DOSAGE_HELP = [
  "O bico da cerca limpa pode ter vazão por metro linear diferente do bico da barra.",
  "Na conta de dosagem, considere a largura da faixa escolhida multiplicada pelo perímetro percorrido.",
  "Se a vazão da cerca limpa for maior que a da barra, a dosagem de referência provavelmente não vai fechar.",
  "Mesmo comparando 1 m de largura da barra, o volume aplicado pode não bater.",
  "Antes de fechar a ordem, confira a vazão do bico da cerca limpa na conta de dosagem.",
];

const MAP_FOOTER_LAYOUT_STORAGE_KEY = "service-order-map-footer-layout-v1";

function readSavedMapFooterLabels() {
  try {
    const raw = localStorage.getItem(MAP_FOOTER_LAYOUT_STORAGE_KEY);
    if (!raw) {
      return {
        cleanFenceLabel: "Usar cerca limpa",
        selectionLabel: "Usar seleção",
      };
    }
    const parsed = JSON.parse(raw);
    return {
      cleanFenceLabel: parsed.cleanFenceLabel || "Usar cerca limpa",
      selectionLabel: parsed.selectionLabel || "Usar seleção",
    };
  } catch (error) {
    return {
      cleanFenceLabel: "Usar cerca limpa",
      selectionLabel: "Usar seleção",
    };
  }
}

function parsePlotAreaHa(plot) {
  const value = Number(String((plot && plot.area_h2) || "").replace(",", "."));
  return Number.isFinite(value) ? value : 0;
}

function uniqueFarmIdsFromPlots(landPlots, plotIds) {
  const idSet = new Set((plotIds || []).map(String));
  const farmIds = new Set();
  (landPlots || []).forEach((plot) => {
    if (idSet.has(String(plot.id)) && plot.farm != null) {
      farmIds.add(Number(plot.farm));
    }
  });
  return Array.from(farmIds);
}

export default function ServiceOrderLocationMapModal({
  open,
  farmId,
  onClose,
  onConfirm,
  onDataRefresh,
  selectedServiceFarmIds,
  selectedLandPlotIds,
  initialCleanFenceWidthM,
  t,
}) {
  const [loading, setLoading] = useState(true);
  const [landPlots, setLandPlots] = useState([]);
  const [farmCenter, setFarmCenter] = useState(null);
  const [savedMapViewport, setSavedMapViewport] = useState(null);
  const [mode, setMode] = useState(MODES.SELECT);
  const [localPlotIds, setLocalPlotIds] = useState([]);
  const [draftPoints, setDraftPoints] = useState([]);
  const [farmName, setFarmName] = useState("");
  const [savingFarm, setSavingFarm] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [cleanFenceConfirmed, setCleanFenceConfirmed] = useState(false);
  const [cleanFencePanelOpen, setCleanFencePanelOpen] = useState(false);
  const [cleanFenceHelpOpen, setCleanFenceHelpOpen] = useState(false);
  const [cleanFenceWidthM, setCleanFenceWidthM] = useState(4);
  const [cleanFenceDraftWidthM, setCleanFenceDraftWidthM] = useState(4);
  const [footerLabels] = useState(() => readSavedMapFooterLabels());
  const [mapReady, setMapReady] = useState(false);

  const mapApiRef = useRef(null);
  const overlaysRef = useRef([]);
  const mapClickListenerRef = useRef(null);
  const cleanFenceWrapRef = useRef(null);

  const { rect, ResizeHandles } = useResizablePanel(open);

  const loadLandPlots = useCallback(async () => {
    if (!farmId) return [];
    return fetchGroupLandPlots(farmId);
  }, [farmId]);

  useEffect(() => {
    if (!open || !farmId) return;

    setLoading(true);
    setMode(MODES.SELECT);
    setDraftPoints([]);
    setFarmName("");
    setLocalPlotIds(Array.isArray(selectedLandPlotIds) ? selectedLandPlotIds : []);
    const savedFenceWidth = Number(initialCleanFenceWidthM) || 0;
    const hasSavedFence = savedFenceWidth > 0;
    setCleanFenceConfirmed(hasSavedFence);
    setCleanFencePanelOpen(false);
    setCleanFenceHelpOpen(false);
    setCleanFenceWidthM(hasSavedFence ? savedFenceWidth : 4);
    setCleanFenceDraftWidthM(hasSavedFence ? savedFenceWidth : 4);
    setMapReady(false);
    setInstruction(t("Clique nos talhões para selecionar onde será feito o serviço."));

    Promise.all([
      axios.get(`${myConfig.API_URL}/farm/${farmId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      }),
      loadLandPlots(),
    ])
      .then(([farmRes, plots]) => {
        setFarmCenter(farmRes.data.farm_center);
        setSavedMapViewport(farmRes.data.map_viewport || null);
        setLandPlots(plots);
      })
      .catch(() => {
        setFarmCenter(null);
        setLandPlots([]);
      })
      .finally(() => setLoading(false));
  }, [open, farmId, loadLandPlots, selectedLandPlotIds, initialCleanFenceWidthM, t]);

  useEffect(() => {
    if (!cleanFencePanelOpen) return undefined;

    function handleDocumentMouseDown(event) {
      if (
        cleanFenceWrapRef.current &&
        !cleanFenceWrapRef.current.contains(event.target)
      ) {
        setCleanFencePanelOpen(false);
        setCleanFenceHelpOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [cleanFencePanelOpen]);

  const initialMapViewport = useMemo(() => {
    if (!farmCenter) return null;
    return resolveMapViewport({
      apiViewport: savedMapViewport,
      localViewport: readLocalMapViewport(
        MAP_VIEWPORT_SCOPES.SERVICE_ORDER_LOCATION,
        farmId
      ),
      fallbackCenter: farmCenter,
      fallbackZoom: 16,
    });
  }, [farmCenter, savedMapViewport, farmId]);

  const selectedSummary = useMemo(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    const widthM = cleanFenceConfirmed ? cleanFenceWidthM : 0;
    return summarizeCleanFenceSelection(
      landPlots,
      localPlotIds,
      widthM,
      maps
    );
  }, [landPlots, localPlotIds, cleanFenceConfirmed, cleanFenceWidthM, mapReady]);

  const cleanFenceDraftSummary = useMemo(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    return summarizeCleanFenceSelection(
      landPlots,
      localPlotIds,
      cleanFenceDraftWidthM,
      maps
    );
  }, [landPlots, localPlotIds, cleanFenceDraftWidthM, mapReady]);

  const previewInsidePlots = useMemo(() => {
    if (mode !== MODES.FARM || draftPoints.length < 3) return [];
    return landPlotsInsideRing(landPlots, draftPoints);
  }, [mode, draftPoints, landPlots]);

  const clearOverlays = useCallback(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    overlaysRef.current.forEach((overlay) => {
      if (!overlay) return;
      if (overlay.setMap) {
        overlay.setMap(null);
        return;
      }
      if (maps && maps.event && maps.event.removeListener) {
        maps.event.removeListener(overlay);
      }
    });
    overlaysRef.current = [];
  }, []);

  const detachMapClick = useCallback(() => {
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (maps && maps.event && mapClickListenerRef.current) {
      maps.event.removeListener(mapClickListenerRef.current);
    }
    mapClickListenerRef.current = null;
  }, []);

  const togglePlot = useCallback((plotId) => {
    setLocalPlotIds((current) => {
      const ids = Array.isArray(current) ? current : [];
      return ids.includes(plotId)
        ? ids.filter((id) => id !== plotId)
        : [...ids, plotId];
    });
  }, []);

  const attachPlotClick = useCallback(
    (maps, overlay, plotId, canSelect) => {
      if (!canSelect || !overlay) return;
      overlaysRef.current.push(
        maps.event.addListener(overlay, "click", (event) => {
          if (event && event.stop) event.stop();
          togglePlot(plotId);
        })
      );
    },
    [togglePlot]
  );

  const renderLandplots = useCallback(() => {
    const map = mapApiRef.current && mapApiRef.current.map;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (!map || !maps) return;

    clearOverlays();
    const selectedSet = new Set(localPlotIds.map(String));
    const insideSet = new Set(previewInsidePlots.map((plot) => String(plot.id)));
    const canSelect = mode === MODES.SELECT;
    const fenceWidth = cleanFenceConfirmed ? cleanFenceWidthM : 0;

    landPlots.forEach((plot) => {
      const ring = parseLandplotRing(plot);
      if (ring.length < 3) return;

      const isSelected = selectedSet.has(String(plot.id));
      const isInsideDraft = insideSet.has(String(plot.id));
      const highlighted = isSelected || isInsideDraft;
      const plotStyle = getLandPlotMapStyle(highlighted);
      const label = getPlotMapLabel(plot);
      const closedRing = closePolygonRing(ring);
      const plotOverlays = [];

      if (isSelected && fenceWidth > 0 && mode === MODES.SELECT) {
        const split = computeCleanFenceSplit(
          ring,
          fenceWidth,
          parsePlotAreaHa(plot),
          maps
        );

        const workArea = new maps.Polygon({
          paths: ring,
          map,
          fillColor: "#A5D6A7",
          fillOpacity: 0.42,
          strokeColor: "#FFD600",
          strokeOpacity: 0.95,
          strokeWeight: 2,
          zIndex: 4,
          clickable: canSelect,
        });
        overlaysRef.current.push(workArea);
        plotOverlays.push(workArea);

        if (split.outerRing && split.outerRing.length >= 3) {
          const fenceBand = new maps.Polygon({
            paths: [closePolygonRing(split.outerRing), closedRing],
            map,
            fillColor: "#FFB74D",
            fillOpacity: 0.5,
            strokeColor: "#FF6F00",
            strokeOpacity: 0.9,
            strokeWeight: 1,
            zIndex: 3,
            clickable: canSelect,
          });
          overlaysRef.current.push(fenceBand);
          plotOverlays.push(fenceBand);
        }

        const perimeterLine = new maps.Polyline({
          path: closedRing,
          map,
          strokeColor: "#FF6F00",
          strokeOpacity: 1,
          strokeWeight: 4,
          zIndex: 5,
          clickable: canSelect,
        });
        overlaysRef.current.push(perimeterLine);
        plotOverlays.push(perimeterLine);
      } else {
        const polygon = new maps.Polygon({
          paths: ring,
          map,
          strokeColor: isInsideDraft ? "#42A5F5" : plotStyle.strokeColor,
          strokeOpacity: plotStyle.strokeOpacity,
          strokeWeight: isSelected ? 4 : plotStyle.strokeWeight,
          fillColor: isInsideDraft ? "#42A5F5" : plotStyle.fillColor,
          fillOpacity: highlighted ? 0.35 : plotStyle.fillOpacity,
          zIndex: highlighted ? 3 : 1,
          clickable: canSelect,
        });
        overlaysRef.current.push(polygon);
        plotOverlays.push(polygon);

        if (isSelected) {
          const perimeterLine = new maps.Polyline({
            path: closedRing,
            map,
            strokeColor: "#FFD600",
            strokeOpacity: 1,
            strokeWeight: 3,
            zIndex: 4,
            clickable: canSelect,
          });
          overlaysRef.current.push(perimeterLine);
          plotOverlays.push(perimeterLine);
        }
      }

      plotOverlays.forEach((overlay) => {
        attachPlotClick(maps, overlay, plot.id, canSelect);
      });

      if (plot.landplot_center && plot.landplot_center.lat != null) {
        const marker = new maps.Marker({
          map,
          position: plot.landplot_center,
          label: {
            text: label,
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "11px",
          },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: canSelect || isInsideDraft ? 14 : 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          zIndex: highlighted ? 4 : 2,
          clickable: canSelect,
          title: label,
        });
        overlaysRef.current.push(marker);
        if (canSelect) {
          overlaysRef.current.push(
            maps.event.addListener(marker, "click", (event) => {
              if (event && event.stop) event.stop();
              togglePlot(plot.id);
            })
          );
        }
      }
    });

    if (mode === MODES.FARM && draftPoints.length > 0) {
      draftPoints.forEach((point, index) => {
        overlaysRef.current.push(
          new maps.Marker({
            position: point,
            map,
            label: {
              text: String(index + 1),
              color: "#ffffff",
              fontWeight: "bold",
            },
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#42A5F5",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
            zIndex: 6,
          })
        );
      });

      if (draftPoints.length >= 2) {
        overlaysRef.current.push(
          new maps.Polyline({
            path: draftPoints,
            map,
            strokeColor: "#42A5F5",
            strokeOpacity: 0.95,
            strokeWeight: 3,
            zIndex: 5,
          })
        );
      }

      if (draftPoints.length >= 3) {
        overlaysRef.current.push(
          new maps.Polygon({
            paths: draftPoints,
            map,
            strokeColor: "#1565C0",
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: "#42A5F5",
            fillOpacity: 0.15,
            zIndex: 4,
            clickable: false,
          })
        );
      }
    }
  }, [
    attachPlotClick,
    cleanFenceConfirmed,
    cleanFenceWidthM,
    clearOverlays,
    draftPoints,
    landPlots,
    localPlotIds,
    mode,
    previewInsidePlots,
    togglePlot,
  ]);

  const attachFarmDrawClick = useCallback(() => {
    detachMapClick();
    const map = mapApiRef.current && mapApiRef.current.map;
    const maps = mapApiRef.current && mapApiRef.current.maps;
    if (!map || !maps || mode !== MODES.FARM) return;

    mapClickListenerRef.current = maps.event.addListener(map, "click", (event) => {
      const point = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setDraftPoints((current) => current.concat(point));
    });
  }, [detachMapClick, mode]);

  useEffect(() => {
    if (!open || loading) return;
    renderLandplots();
    attachFarmDrawClick();
    return () => {
      detachMapClick();
    };
  }, [open, loading, renderLandplots, attachFarmDrawClick, detachMapClick]);

  useEffect(() => {
    if (mode === MODES.SELECT) {
      setInstruction(
        t("Clique nos talhões para selecionar onde será feito o serviço.")
      );
    } else if (draftPoints.length < 3) {
      setInstruction(
        t("Clique no mapa para desenhar o perímetro da fazenda (mínimo 3 pontos).")
      );
    } else {
      const areaHa = computePointsAreaHa(
        draftPoints,
        mapApiRef.current && mapApiRef.current.maps
      );
      setInstruction(
        `${previewInsidePlots.length} ${t("talhão(ões) dentro do perímetro")} · ${areaHa.toFixed(2)} ha`
      );
    }
  }, [mode, draftPoints, previewInsidePlots.length, t]);

  function handleGoogleApiLoaded({ map, maps, google }) {
    mapApiRef.current = { map, maps, google };
    setMapReady(true);
    if (initialMapViewport) {
      applyViewportToGoogleMap(map, initialMapViewport);
    }
    renderLandplots();
    attachFarmDrawClick();
  }

  function handleSaveMapViewport() {
    const map = mapApiRef.current && mapApiRef.current.map;
    if (!map) return;
    const viewport = readViewportFromGoogleMap(map);
    if (!viewport) return;
    writeLocalMapViewport(
      MAP_VIEWPORT_SCOPES.SERVICE_ORDER_LOCATION,
      farmId,
      viewport
    );
    toast.success(t("Posição do mapa salva."), { position: "bottom-right" });
  }

  function handleOpenCleanFencePanel() {
    setCleanFenceDraftWidthM(cleanFenceConfirmed ? cleanFenceWidthM : 4);
    setCleanFencePanelOpen(true);
  }

  function handleConfirmCleanFence() {
    setCleanFenceWidthM(cleanFenceDraftWidthM);
    setCleanFenceConfirmed(true);
    setCleanFencePanelOpen(false);
  }

  function handleCleanFenceCheckboxChange(event) {
    if (event.target.checked) {
      handleOpenCleanFencePanel();
      return;
    }
    setCleanFenceConfirmed(false);
    setCleanFencePanelOpen(false);
    setCleanFenceHelpOpen(false);
  }

  function handleDismissCleanFencePanel() {
    setCleanFencePanelOpen(false);
    setCleanFenceHelpOpen(false);
  }

  function handleClose() {
    setCleanFencePanelOpen(false);
    setCleanFenceHelpOpen(false);
    onClose();
    releaseModalBlockers();
  }

  function handleConfirmSelection() {
    onConfirm({
      serviceFarmIds: uniqueFarmIdsFromPlots(landPlots, localPlotIds),
      landPlotIds: localPlotIds,
      cleanFenceWidthM: cleanFenceConfirmed ? cleanFenceWidthM : 0,
      areaSummary: selectedSummary,
    });
    handleClose();
  }

  async function handleSaveGeorefFarm() {
    const name = farmName.trim();
    if (!name) {
      toast.error(t("Informe o nome da fazenda."), { position: "bottom-right" });
      return;
    }
    if (draftPoints.length < 3) {
      toast.error(t("Desenhe o perímetro com pelo menos 3 pontos."), {
        position: "bottom-right",
      });
      return;
    }

    setSavingFarm(true);
    try {
      const created = await createGroupFarm(
        farmId,
        name,
        pathToPolygonWkt(draftPoints),
        { alwaysCreate: true }
      );
      const plots = await loadLandPlots();
      setLandPlots(plots);
      if (onDataRefresh) onDataRefresh();

      const assignedIds = Array.isArray(created.assigned_land_plot_ids)
        ? created.assigned_land_plot_ids
        : previewInsidePlots.map((plot) => plot.id);

      setLocalPlotIds(assignedIds);
      toast.success(
        `${t("Fazenda salva.")} ${assignedIds.length} ${t("talhão(ões) vinculado(s).")}`,
        { position: "bottom-right" }
      );
      setMode(MODES.SELECT);
      setDraftPoints([]);
      setFarmName("");
    } catch (error) {
      toast.error(t("Não foi possível salvar a fazenda."), {
        position: "bottom-right",
      });
    } finally {
      setSavingFarm(false);
    }
  }

  const draftMetrics =
    draftPoints.length >= 3
      ? {
          areaHa: computePointsAreaHa(
            draftPoints,
            mapApiRef.current && mapApiRef.current.maps
          ),
          perimeterM: computePointsPerimeterM(
            draftPoints,
            mapApiRef.current && mapApiRef.current.maps
          ),
        }
      : null;

  return (
    <ServiceOrderMapOverlay
      open={open}
      onClose={handleClose}
      ariaLabelledBy="service-order-location-map-title"
    >
      <div
        className="track-heading-map-modal service-order-location-map-modal"
        style={{
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
        }}
      >
        <ResizeHandles />

        <div className="track-heading-map-modal__header">
          <h2 id="service-order-location-map-title">
            <FaMapMarkedAlt style={{ marginRight: 8 }} />
            {t("Local do serviço no mapa")}
          </h2>
          <button
            type="button"
            className="track-heading-map-modal__close"
            onClick={handleClose}
          >
            <FaTimes />
          </button>
        </div>

        <div className="service-order-location-map-modal__toolbar">
          <button
            type="button"
            className={`btn btn-sm ${
              mode === MODES.SELECT ? "btn-primary" : "btn-default"
            }`}
            onClick={() => {
              setMode(MODES.SELECT);
              setDraftPoints([]);
            }}
          >
            <FaTh style={{ marginRight: 6 }} />
            {t("Selecionar talhões")}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${
              mode === MODES.FARM ? "btn-primary" : "btn-default"
            }`}
            onClick={() => setMode(MODES.FARM)}
          >
            <FaDrawPolygon style={{ marginRight: 6 }} />
            {t("Cadastrar fazenda no mapa")}
          </button>
          {mode === MODES.FARM && draftPoints.length > 0 ? (
            <button
              type="button"
              className="btn btn-sm btn-default"
              onClick={() => setDraftPoints([])}
            >
              <FaUndo style={{ marginRight: 6 }} />
              {t("Limpar desenho")}
            </button>
          ) : null}
        </div>

        <p className="track-heading-map-modal__hint">{instruction}</p>

        {mode === MODES.SELECT && selectedSummary.count > 0 ? (
          <div className="service-order-location-map-modal__summary">
            <strong>
              {selectedSummary.count} {t("talhão(ões)")} ·{" "}
              {selectedSummary.totalHa.toFixed(2)} ha {t("total")}
            </strong>
            {cleanFenceConfirmed ? (
              <div className="service-order-location-map-modal__summary-split">
                <span>
                  {t("Talhões")}: {selectedSummary.workHa.toFixed(2)} ha
                </span>
                <span>
                  {CLEAN_FENCE_NAME} ({cleanFenceWidthM} m): +{" "}
                  {selectedSummary.fenceHa.toFixed(2)} ha
                </span>
                <span>
                  {t("Total")}: {selectedSummary.totalHa.toFixed(2)} ha
                </span>
              </div>
            ) : (
              <div className="service-order-location-map-modal__summary-split">
                <span>
                  {t("Perímetro visível nos talhões selecionados")}
                </span>
              </div>
            )}
          </div>
        ) : null}

        {mode === MODES.FARM ? (
          <div className="service-order-location-map-modal__farm-form">
            <input
              type="text"
              className="form-control input-sm"
              value={farmName}
              onChange={(event) => setFarmName(event.target.value)}
              placeholder={t("Nome da fazenda (ex.: Fazenda San Rafael)")}
              disabled={savingFarm}
            />
            {draftMetrics ? (
              <span className="service-order-location-map-modal__metrics">
                {draftMetrics.areaHa.toFixed(2)} ha ·{" "}
                {draftMetrics.perimeterM.toFixed(0)} m ·{" "}
                {previewInsidePlots.length} {t("talhões")}
              </span>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={handleSaveGeorefFarm}
              disabled={savingFarm}
            >
              {savingFarm ? t("Salvando...") : t("Salvar fazenda e vincular talhões")}
            </button>
          </div>
        ) : null}

        <div className="track-heading-map-modal__map">
          {loading || !farmCenter ? (
            <div className="rumo-spinner">
              <MDSpinner />
            </div>
          ) : (
            <div className="rumo-map-wrapper track-heading-map-modal__map-inner">
              <GoogleMapReact
                key={`service-order-location-map-${farmId}-${mode}`}
                bootstrapURLKeys={{ key: myConfig.GOOGLE_API_KEY }}
                options={getMapOptions}
                defaultCenter={
                  initialMapViewport
                    ? {
                        lat: initialMapViewport.lat,
                        lng: initialMapViewport.lng,
                      }
                    : { lat: farmCenter.ltd, lng: farmCenter.lng }
                }
                defaultZoom={initialMapViewport ? initialMapViewport.zoom : 16}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={handleGoogleApiLoaded}
              />
              <MapViewportToolbar
                onSave={handleSaveMapViewport}
                disabled={loading || !farmCenter}
                label={t("Salvar posição do mapa")}
              />
            </div>
          )}
        </div>

        <div className="service-order-location-map-modal__footer">
          <div />
          <div className="service-order-location-map-modal__footer-actions">
            <button type="button" className="btn btn-default" onClick={handleClose}>
              {t("Cancelar")}
            </button>
            {mode === MODES.SELECT ? (
              <div
                className="service-order-location-map-modal__clean-fence-wrap"
                ref={cleanFenceWrapRef}
              >
                <div
                  className={`service-order-location-map-modal__action-btn is-row${
                    cleanFenceConfirmed ? " is-active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    className="service-order-location-map-modal__clean-fence-checkbox"
                    checked={cleanFenceConfirmed || cleanFencePanelOpen}
                    onChange={handleCleanFenceCheckboxChange}
                    aria-label={CLEAN_FENCE_ACTION_LABEL}
                  />
                  <button
                    type="button"
                    className="service-order-location-map-modal__clean-fence-open"
                    onClick={handleOpenCleanFencePanel}
                  >
                    <FaBorderStyle size={18} />
                    <span>{footerLabels.cleanFenceLabel || CLEAN_FENCE_ACTION_LABEL}</span>
                  </button>
                </div>
                {cleanFencePanelOpen ? (
                  <div className="service-order-location-map-modal__clean-fence-panel">
                    <div className="service-order-location-map-modal__clean-fence-panel-header">
                      <div className="service-order-location-map-modal__clean-fence-panel-title">
                        {CLEAN_FENCE_ACTION_LABEL}
                      </div>
                      <button
                        type="button"
                        className="service-order-location-map-modal__clean-fence-panel-close"
                        onClick={handleDismissCleanFencePanel}
                        aria-label={t("Fechar")}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <div className="service-order-location-map-modal__fence-widths">
                      <span className="service-order-location-map-modal__fence-label">
                        {t("Largura da faixa")}: {cleanFenceDraftWidthM} m
                      </span>
                      <button
                        type="button"
                        className="service-order-location-map-modal__fence-info-btn"
                        aria-label={t("Observação sobre dosagem da cerca limpa")}
                        aria-expanded={cleanFenceHelpOpen}
                        onClick={() => setCleanFenceHelpOpen((open) => !open)}
                      >
                        <FaInfoCircle />
                      </button>
                      {CLEAN_FENCE_WIDTH_OPTIONS.map((width) => (
                        <button
                          key={width}
                          type="button"
                          className={`btn btn-xs ${
                            cleanFenceDraftWidthM === width
                              ? "btn-success"
                              : "btn-default"
                          }`}
                          onClick={() => setCleanFenceDraftWidthM(width)}
                        >
                          {width} m
                        </button>
                      ))}
                    </div>
                    <p className="service-order-location-map-modal__clean-fence-note">
                      {t("Faixa de")} {cleanFenceDraftWidthM} m{" "}
                      {t("somada fora do perímetro dos talhões")}
                      {cleanFenceDraftSummary.fenceHa > 0
                        ? ` (+${cleanFenceDraftSummary.fenceHa.toFixed(2)} ha).`
                        : "."}
                    </p>
                    {cleanFenceHelpOpen ? (
                      <div className="service-order-location-map-modal__clean-fence-help">
                        <strong>{t("Observação sobre dosagem")}</strong>
                        <ul>
                          {CLEAN_FENCE_DOSAGE_HELP.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="service-order-location-map-modal__clean-fence-confirm btn btn-success btn-sm"
                      onClick={handleConfirmCleanFence}
                    >
                      <FaCheck style={{ marginRight: 6 }} />
                      {t("Confirmar")} {CLEAN_FENCE_NAME}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {mode === MODES.SELECT ? (
              <button
                type="button"
                className="service-order-location-map-modal__action-btn is-row is-primary"
                onClick={handleConfirmSelection}
              >
                <FaCheck size={18} />
                <span>
                  {footerLabels.selectionLabel}
                  {selectedSummary.count > 0
                    ? cleanFenceConfirmed
                      ? ` (${selectedSummary.workHa.toFixed(2)} + ${selectedSummary.fenceHa.toFixed(2)} = ${selectedSummary.totalHa.toFixed(2)} ha)`
                      : ` (${selectedSummary.count} · ${selectedSummary.totalHa.toFixed(2)} ha)`
                    : ""}
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </ServiceOrderMapOverlay>
  );
}
